import { Store } from '@mui/icons-material';
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Chip,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Typography
} from '@mui/material';
import { memo } from 'react';

interface ShopStatusSectionProps {
  value: string;
  onChange: (value: string) => void;
}

const statusOptions = [
  {
    value: "WAIT_FOR_REVIEW",
    label: "Waiting for Review",
    description: "Shop is still being set up",
    chipColor: "default" as const,
  },
  {
    value: "REVIEWING",
    label: "Reviewing",
    description: "Shop is live and accepting bookings",
    chipColor: "warning" as const,
  },
  {
    value: "ACTIVE",
    label: "Active",
    description: "Shop is live and accepting bookings",
    chipColor: "success" as const,
  },
  {
    value: "REJECTED",
    label: "Disabled",
    description: "Shop is temporarily closed",
    chipColor: "error" as const,
  },
];

const ShopStatusSectionComponent = ({ value, onChange }: ShopStatusSectionProps) => {
  return (
    <Card elevation={2}>
      <CardHeader
        avatar={<Store sx={{ color: '#ba2156' }} />}
        title={
          <Typography variant="h6" component="h3">
            Shop Status
          </Typography>
        }
        subheader={
          <Typography variant="body2" color="text.secondary">
            Control your salon's availability and visibility to customers
          </Typography>
        }
      />
      <CardContent>
        <FormControl component="fieldset">
          <RadioGroup value={value} onChange={(e) => onChange(e.target.value)}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {statusOptions.map((option) => (
                <Box
                  key={option.value}
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'grey.300',
                    borderRadius: 2,
                    '&:hover': { bgcolor: 'action.hover' },
                    transition: 'background-color 0.2s'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <FormControlLabel
                      value={option.value}
                      control={
                        <Radio 
                          sx={{ 
                            color: '#ba2156', 
                            '&.Mui-checked': { color: '#ba2156' } 
                          }} 
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip 
                            label={option.label} 
                            size="small" 
                            color={option.chipColor}
                          />
                        </Box>
                      }
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                    {option.description}
                  </Typography>
                </Box>
              ))}
            </Box>
          </RadioGroup>
        </FormControl>
      </CardContent>
    </Card>
  );
};

export const ShopStatusSection = memo(ShopStatusSectionComponent);